import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { useOrgStore } from '@/stores/orgStore';
import { isActiveSubscription } from '@/lib/subscriptionStatus';
import { useMfa } from '@/hooks/useMfa';
import { PageLoader } from '@/components/ui/loading-spinner';
import { StartupErrorScreen } from '@/components/auth/StartupErrorScreen';
import { ProfileCompletionGate } from '@/components/auth/ProfileCompletionGate';
import { ProfileCompletionBanner } from '@/components/auth/ProfileCompletionBanner';
import { captureApplicationError } from '@/lib/monitoring';
import { useRole } from '@/hooks/useRole';
import { useProfileOnboarding } from '@/hooks/useProfileOnboarding';
import { getProfileOnboardingDecision, isOwnerSetupPath } from '@/lib/profileOnboarding';

/**
 * Mirrors the v1 onboarding gate chain:
 * 1. Email verified
 * 2. MFA verified (if enrolled)
 * 3. Role-aware profile completion, with two server-backed deferrals
 * 4. Subscription/trial valid
 *
 * Bypass pages: /profile, /employee/availability, /bo/billing, /bo/offboarding, /trial-expired
 */
export function OnboardingGuard() {
  const user = useAuthStore((s) => s.user);
  const session = useAuthStore((s) => s.session);
  const authLoading = useAuthStore((s) => s.isLoading);
  const authError = useAuthStore((s) => s.error);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const org = useOrgStore((s) => s.organization);
  const memberRole = useOrgStore((s) => s.memberRole);
  const orgLoading = useOrgStore((s) => s.isLoading);
  const orgError = useOrgStore((s) => s.error);
  const location = useLocation();
  const roleState = useRole();
  const {
    needsVerification,
    isLoadingFactors,
    error: mfaError,
  } = useMfa({
    enabled: isAuthenticated && !!user,
  });

  const onboardingQuery = useProfileOnboarding({
    userId: user?.id,
    accessToken: session?.access_token,
    organizationId: org?.id ?? null,
    role: roleState.role,
    enabled: isAuthenticated && !!user,
  });

  useEffect(() => {
    if (onboardingQuery.error) {
      captureApplicationError(onboardingQuery.error, {
        tags: { area: 'onboarding', operation: 'completion_check' },
      });
    }
  }, [onboardingQuery.error]);

  useEffect(() => {
    if (isAuthenticated && mfaError) {
      captureApplicationError(mfaError, {
        tags: { area: 'onboarding', operation: 'mfa_check' },
      });
    }
  }, [isAuthenticated, mfaError]);

  const isLoading =
    authLoading ||
    (isAuthenticated &&
      (orgLoading || isLoadingFactors || roleState.isLoading || onboardingQuery.isLoading));

  if (isLoading) {
    return <PageLoader />;
  }

  if (authError) {
    return <StartupErrorScreen title="Session needs attention" message={authError} />;
  }

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (orgError) {
    return <StartupErrorScreen title="Workspace could not load" message={orgError} />;
  }

  if (onboardingQuery.error) {
    return (
      <StartupErrorScreen
        title="Profile check could not load"
        message="We could not check your required profile information. Refresh the page, or sign out and back in if the issue continues."
      />
    );
  }

  if (mfaError) {
    return (
      <StartupErrorScreen
        title="Security check could not load"
        message="We could not check your account security status. Refresh the page, or sign out and back in if the issue continues."
      />
    );
  }

  // Bypass pages skip gates 3-5 to prevent redirect loops.
  const bypassPaths = [
    '/profile',
    // A person's own record outlives the organisation, so it is reachable whatever has
    // happened to the subscription, and whether or not they still belong to one.
    '/my-record',
    '/employee/availability',
    '/bo/billing',
    '/bo/offboarding',
    '/trial-expired',
  ];
  const isOnBypassPage = bypassPaths.some((p) => location.pathname.startsWith(p));

  // Gate 1: Email verification
  if (user && !user.email_confirmed_at) {
    return <Navigate to={`/verify-email?email=${encodeURIComponent(user.email ?? '')}`} replace />;
  }

  // Gate 2: MFA verification
  if (needsVerification) {
    const returnTo = `${location.pathname}${location.search}${location.hash}`;
    return <Navigate to="/verify-mfa" state={{ from: returnTo }} replace />;
  }

  // Gate 3: Subscription/trial check.
  //
  // Ahead of the profile gate on purpose. A brand-new owner has no personal details yet, and
  // the branch below lets them past to set their organisation up; before a card that would
  // hand them a workspace they cannot use (20261030000000_card_before_access). Billing is a
  // bypass page, so there is no loop.
  if (!isOnBypassPage && org) {
    const subActive = isActiveSubscription(org.subscription_status);
    const trialEnd = org.trial_ends_at ? new Date(org.trial_ends_at) : null;
    const inTrial = trialEnd && new Date() < trialEnd;

    if (!subActive && !inTrial) {
      return memberRole === 'BO' ? (
        <Navigate to="/bo/billing" replace />
      ) : (
        <Navigate to="/trial-expired" replace />
      );
    }
  }

  if (!isOnBypassPage && onboardingQuery.data) {
    const decision = getProfileOnboardingDecision({
      summary: onboardingQuery.data.summary,
      deferralCount: onboardingQuery.data.progress?.deferral_count ?? 0,
      deferredInCurrentSession: onboardingQuery.data.deferredInCurrentSession,
    });

    if (decision.state !== 'ALLOW') {
      // An owner can still set their organisation up; pay, approvals and documents wait.
      // Only the hard personal gate is relaxed: later sections keep their own dialog.
      if (
        decision.state === 'REQUIRE_PERSONAL' &&
        memberRole === 'BO' &&
        isOwnerSetupPath(location.pathname)
      ) {
        return (
          <>
            <Outlet />
            <ProfileCompletionBanner section={decision.section} />
          </>
        );
      }

      return (
        <>
          <Outlet />
          <ProfileCompletionGate
            decision={decision}
            summary={onboardingQuery.data.summary}
            isDeferring={onboardingQuery.isDeferring}
            deferError={onboardingQuery.deferError}
            onDefer={onboardingQuery.defer}
          />
        </>
      );
    }
  }

  // Gate 5: somebody with no organisation at all. Their own record is all that is left
  // to show them, and every workspace page below would render empty without it.
  //
  // A Platform Admin is the exception, and deliberately so: their session never carries
  // an organisation, because inheriting one would mean inheriting its data and its
  // branding. Sending them to a personal record they will never have anything in locked
  // them out of the console entirely.
  if (!isOnBypassPage && !org && !orgLoading && !roleState.isAdmin) {
    return <Navigate to="/my-record" replace />;
  }

  return <Outlet />;
}
